import type { ReactNode, SVGProps } from 'react';

/* ------------------------------------------------------------------ *
 * A drawn icon set, not an installed one.
 *
 * Material Icons / Lucide / Heroicons all carry the visual accent of
 * the system they were drawn for, and a rounded 2px-stroke icon set
 * would undo the type and surface work on its own. These are 16×16 on
 * a whole-pixel grid, 1.25 stroke, butt caps, mitre joins — the same
 * hairline the rules and borders use, so an icon reads as part of the
 * ruling rather than as an illustration sitting on top of it.
 *
 * They are also domain-specific where it matters: a crate, a scale, a
 * van, a market stall — not a generic shopping bag.
 * ------------------------------------------------------------------ */

type IconProps = SVGProps<SVGSVGElement>;

const base = {
  viewBox: '0 0 16 16',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.25,
  strokeLinecap: 'butt',
  strokeLinejoin: 'miter',
  width: '1em',
  height: '1em',
} as const;

const Icon = (path: ReactNode) =>
  function IconComponent(props: IconProps) {
    return (
      <svg {...base} aria-hidden="true" focusable="false" {...props}>
        {path}
      </svg>
    );
  };

/* --- Navigation --------------------------------------------------- */

/** Dashboard: a ledger page ruled into panels. */
export const IconBoard = Icon(
  <>
    <rect x="1.5" y="2" width="13" height="12" />
    <path d="M1.5 6h13M6.5 6v8" />
  </>,
);

/** Orders: a stacked order pad. */
export const IconOrders = Icon(
  <>
    <path d="M3.5 1.5h9v13l-2-1.4-2 1.4-2-1.4-2 1.4z" />
    <path d="M6 5h4M6 8h4" />
  </>,
);

/** Market: a stall awning over a counter. */
export const IconMarket = Icon(
  <>
    <path d="M1.5 5.5 3 1.5h10l1.5 4z" />
    <path d="M2.5 5.5v9h11v-9" />
    <path d="M5.5 14.5v-4h5v4" />
  </>,
);

/** Purchasing: a crate with a handle cut-out. */
export const IconCrate = Icon(
  <>
    <path d="M1.5 4.5h13v9a1 1 0 0 1-1 1h-11a1 1 0 0 1-1-1z" />
    <path d="M1.5 4.5 3 1.5h10l1.5 3" />
    <path d="M6 8h4" />
  </>,
);

/** Logistics: a delivery van. */
export const IconVan = Icon(
  <>
    <path d="M.5 3.5h9v8h-9zM9.5 6h3l3 2.5v3h-6z" />
    <circle cx="4" cy="12" r="1.4" />
    <circle cx="12" cy="12" r="1.4" />
  </>,
);

/** Grouping: many order lines funnelling into one crate. */
export const IconGrouping = Icon(
  <>
    <path d="M2 2.5h12M4 5.5h8M6 8.5h4" />
    <path d="M3.5 11h9v3.5h-9z" />
  </>,
);

/** Suppliers: goods arriving into a crate. */
export const IconSupplier = Icon(
  <>
    <path d="M8 1v5.5M5.5 4 8 6.5 10.5 4" />
    <path d="M1.5 8.5h13v6h-13z" />
    <path d="M6 11.5h4" />
  </>,
);

/** Delivery list: a clipboard with ticked stops. */
export const IconDeliveryList = Icon(
  <>
    <path d="M3 3.5h10v11H3z" />
    <path d="M6 1.5h4v2H6z" />
    <path d="M5 7l1 1 1.7-2M9 7.5h3M5 11l1 1 1.7-2M9 11.5h3" />
  </>,
);

/** Catalog: a card index. */
export const IconCatalog = Icon(
  <>
    <rect x="1.5" y="3.5" width="13" height="10" />
    <path d="M4.5 1.5v4M11.5 1.5v4M1.5 7h13" />
  </>,
);

/** Products: a scale pan on a beam. */
export const IconScale = Icon(
  <>
    <path d="M8 1.5v13M4 14.5h8" />
    <path d="M2 5h12" />
    <path d="M1 9.5 3.5 5 6 9.5a2.5 2.5 0 0 1-5 0ZM10 9.5 12.5 5 15 9.5a2.5 2.5 0 0 1-5 0Z" />
  </>,
);

/** Stock: stacked crates. */
export const IconStock = Icon(
  <>
    <rect x="1.5" y="8.5" width="6" height="5.5" />
    <rect x="8.5" y="8.5" width="6" height="5.5" />
    <rect x="5" y="2.5" width="6" height="5.5" />
  </>,
);

/** Contacts: two figures, one behind. */
export const IconContacts = Icon(
  <>
    <circle cx="6" cy="5" r="2.5" />
    <path d="M1.5 14c0-2.5 2-4.5 4.5-4.5s4.5 2 4.5 4.5" />
    <path d="M11 3.2a2.5 2.5 0 0 1 0 4.6M12 9.8c1.6.6 2.5 2.2 2.5 4.2" />
  </>,
);

/** Messaging: overlapping speech blocks, square. */
export const IconMessages = Icon(
  <>
    <path d="M1.5 2.5h9v6h-5l-2.5 2v-2h-1.5z" />
    <path d="M5.5 10.5h9v-6h-2" />
  </>,
);

/** Schedule: a wall calendar. */
export const IconCalendar = Icon(
  <>
    <rect x="1.5" y="3" width="13" height="11.5" />
    <path d="M1.5 6.5h13M4.5 1.5v3M11.5 1.5v3" />
  </>,
);

/** Invoices: a document with a total rule. */
export const IconInvoice = Icon(
  <>
    <path d="M3 1.5h10v13H3z" />
    <path d="M5.5 5h5M5.5 7.5h5M5.5 11h5" />
  </>,
);

/** Payments: coins in a column. */
export const IconPayments = Icon(
  <>
    <ellipse cx="8" cy="3.5" rx="6" ry="2" />
    <path d="M2 3.5v4c0 1.1 2.7 2 6 2s6-.9 6-2v-4" />
    <path d="M2 7.5v4c0 1.1 2.7 2 6 2s6-.9 6-2v-4" />
  </>,
);

/** Analytics: a bar chart on an axis. */
export const IconAnalytics = Icon(
  <>
    <path d="M1.5 1.5v13h13" />
    <path d="M4.5 12V8M7.5 12V4.5M10.5 12V9.5M13.5 12V6.5" />
  </>,
);

/** Team: a badge. */
export const IconTeam = Icon(
  <>
    <rect x="2" y="3.5" width="12" height="10" />
    <circle cx="6" cy="7" r="1.6" />
    <path d="M3.5 11.5c0-1.4 1.1-2.5 2.5-2.5s2.5 1.1 2.5 2.5M10 6.5h2.5M10 9h2.5" />
  </>,
);

export const IconSettings = Icon(
  <>
    <circle cx="8" cy="8" r="2.5" />
    <path d="M8 1v2M8 13v2M15 8h-2M3 8H1M12.9 3.1l-1.4 1.4M4.5 11.5l-1.4 1.4M12.9 12.9l-1.4-1.4M4.5 4.5 3.1 3.1" />
  </>,
);

/* --- Actions ------------------------------------------------------ */

export const IconSearch = Icon(
  <>
    <circle cx="7" cy="7" r="4.5" />
    <path d="M10.5 10.5 15 15" />
  </>,
);

export const IconPlus = Icon(<path d="M8 2.5v11M2.5 8h11" />);
export const IconMinus = Icon(<path d="M2.5 8h11" />);
export const IconClose = Icon(<path d="M3.5 3.5l9 9M12.5 3.5l-9 9" />);
export const IconCheck = Icon(<path d="M2.5 8.5 6 12l7.5-8" />);
export const IconChevronDown = Icon(<path d="M3 5.5 8 10.5l5-5" />);
export const IconChevronRight = Icon(<path d="M5.5 3 10.5 8l-5 5" />);
export const IconMore = Icon(
  <>
    <circle cx="3" cy="8" r=".9" fill="currentColor" stroke="none" />
    <circle cx="8" cy="8" r=".9" fill="currentColor" stroke="none" />
    <circle cx="13" cy="8" r=".9" fill="currentColor" stroke="none" />
  </>,
);
export const IconFilter = Icon(<path d="M1.5 3h13l-5 5.5V14l-3-1.5V8.5z" />);
export const IconExport = Icon(
  <>
    <path d="M8 1.5v9M4.5 5 8 1.5 11.5 5" />
    <path d="M1.5 10.5v3h13v-3" />
  </>,
);
export const IconPrint = Icon(
  <>
    <path d="M4 6V1.5h8V6" />
    <rect x="1.5" y="6" width="13" height="5.5" />
    <path d="M4 9.5h8v5H4z" />
  </>,
);
export const IconWarn = Icon(
  <>
    <path d="M8 1.5 15 14H1z" />
    <path d="M8 6v4M8 11.6v.9" />
  </>,
);
export const IconClock = Icon(
  <>
    <circle cx="8" cy="8" r="6.5" />
    <path d="M8 4v4.2l2.8 1.8" />
  </>,
);
export const IconLink = Icon(
  <>
    <path d="M6.5 9.5a3 3 0 0 0 4.2 0l2.3-2.3a3 3 0 0 0-4.2-4.2L7.5 4.3" />
    <path d="M9.5 6.5a3 3 0 0 0-4.2 0L3 8.8a3 3 0 0 0 4.2 4.2l1.3-1.3" />
  </>,
);

/* --- Channels ------------------------------------------------------
   Deliberately schematic rather than brand marks: reproducing the
   WhatsApp or Telegram logo inside a third-party UI is a trademark
   question, and a monoline glyph sits better with everything else. */

export const IconWhatsapp = Icon(
  <>
    <path d="M2 14l1-3.2A6 6 0 1 1 5.4 13z" />
    <path d="M5.8 6c0 2.4 1.8 4.2 4.2 4.2" />
  </>,
);
export const IconTelegram = Icon(<path d="M1.5 7.5 14.5 2l-2 12-4-3.5-2.5 2.5v-3.5z" />);
export const IconEmail = Icon(
  <>
    <rect x="1.5" y="3" width="13" height="10" />
    <path d="m1.5 4 6.5 5 6.5-5" />
  </>,
);
export const IconPhone = Icon(
  <path d="M3 1.5h3l1 3.5-2 1a9 9 0 0 0 4.5 4.5l1-2 3.5 1v3h-1A12 12 0 0 1 2 2.5z" />,
);
export const IconSms = Icon(
  <>
    <rect x="1.5" y="2.5" width="13" height="9" />
    <path d="M4.5 14.5 7 11.5" />
  </>,
);

export const CHANNEL_ICONS = {
  whatsapp: IconWhatsapp,
  telegram: IconTelegram,
  email: IconEmail,
  sms: IconSms,
  phone: IconPhone,
  signal: IconMessages,
  instagram: IconMessages,
} as const;
